import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import Header from "../Components/Header";
import "./ProductDetails.css";
import axios from "axios";
import { API_BASE } from "../config";

const DEFAULT_DETAILS = {
  length: "",
  washCare: "",
  sleeves: "",
  neckline: "",
  dressInfo: "",
  disclaimer: "",
  whatYouWillReceive: "",
  customDetails: [],
};

const COLOR_HEX = {
  red: "#D32F2F",
  maroon: "#800000",
  pink: "#EC407A",
  orange: "#F57C00",
  yellow: "#FBC02D",
  green: "#388E3C",
  blue: "#1976D2",
  navy: "#1A237E",
  purple: "#7B1FA2",
  black: "#111111",
  white: "#FFFFFF",
  beige: "#D7CCC8",
  gold: "#D4AF37",
  grey: "#757575",
  gray: "#757575",
};

const normalizeSizes = (sizes) => {
  if (!Array.isArray(sizes)) {
    return [];
  }

  return sizes
    .map((item) =>
      typeof item === "string"
        ? item
        : item?.size
    )
    .filter(Boolean);
};

const normalizeDetails = (details) => {
  if (!details) {
    return DEFAULT_DETAILS;
  }

  return {
    length: details.length ?? "",
    washCare: details.washCare ?? "",
    sleeves: details.sleeves ?? "",
    neckline: details.neckline ?? "",
    dressInfo: details.dressInfo ?? "",
    disclaimer: details.disclaimer ?? "",
    whatYouWillReceive:
      details.whatYouWillReceive ?? "",

    customDetails: Array.isArray(
      details.customDetails
    )
      ? details.customDetails
      : [],
  };
};

const getImageUrl = (image) => {
  if (typeof image === "string") {
    return image;
  }

  return image?.imageUrl || "";
};

const ProductDetails = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const location = useLocation();

  const [product, setProduct] = useState(null);

  const [activeImg, setActiveImg] = useState("");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(
          `${API_BASE}/Products/${id}`
        );

        const data = response.data;

        setProduct({
          ...data,

          details: normalizeDetails(
            data.details
          ),
        });

        const images = Array.isArray(data.images)
          ? data.images
          : [];

        const mainImage =
          images.find(
            (image) => image?.isMain
          )?.imageUrl ||
          images[0]?.imageUrl ||
          "";

        setActiveImg(mainImage);
      } catch (error) {
        console.error(
          "Error fetching product:",
          error
        );

        if (
          error?.response?.status === 404
        ) {
          alert("Product not found.");
          navigate("/products");
        }
      }
    };

    fetchProduct();
  }, [id, navigate]);

  if (!product) {
    return (
      <div className="productdetails-wrapper">
        <div className="productdetails-container">
          <Header />

          <div className="productdetails-loading">
            Loading product details...
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // NORMALIZED PRODUCT DATA
  // =====================================================

  const sizes = normalizeSizes(
    product.sizes
  );

  const colors = Array.isArray(
    product.colors
  )
    ? product.colors
    : [];

  const images = Array.isArray(
    product.images
  )
    ? product.images
    : [];

  // =====================================================
  // PRICING
  // =====================================================

  const originalPrice =
    Number(product.originalPrice) || 0;

  const discountPercentage =
    Number(product.discountPercentage) || 0;

  // Backend calculated value is authoritative.
  // Fallback calculation is only for old products
  // where finalPrice may not exist.
  const finalPrice =
    product.finalPrice !== null &&
      product.finalPrice !== undefined
      ? Number(product.finalPrice)
      : originalPrice -
      (originalPrice *
        discountPercentage) /
      100;

  const hasDiscount =
    discountPercentage > 0 &&
    finalPrice < originalPrice;

  const discountAmount =
    Math.max(
      originalPrice - finalPrice,
      0
    );

  // =====================================================
  // STATUS
  // =====================================================

  const statusText =
    Number(product.status) === 1
      ? "Active"
      : "Out Of Stock";

  // =====================================================
  // DETAILS
  // =====================================================

  const details =
    product.details || DEFAULT_DETAILS;

  const customDetails =
    Array.isArray(
      details.customDetails
    )
      ? details.customDetails.filter(
        (detail) =>
          detail?.label ||
          detail?.value
      )
      : [];

  return (
    <div className="productdetails-wrapper">
      <div className="productdetails-container">
        <Header />

        {/* ================= TOP BAR ================= */}

        <div className="productdetails-top-bar">
          <div>
            <h2>
              Product Details
            </h2>

            <p className="productdetails-sub">
              View complete product information
            </p>
          </div>

          <div className="productdetails-top-actions">
            {/* DELETE */}

            <button
              className="productdetails-delete-btn"
              onClick={() =>
                navigate(
                  "/delete-popup",
                  {
                    state: {
                      background:
                        location,

                      id: product.id,

                      name:
                        product.name,
                    },
                  }
                )
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 -960 960 960"
                width="24px"
                fill="#FFFFFF"
              >
                <path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z" />
              </svg>

              Delete
            </button>

            {/* EDIT */}

            <button
              className="productdetails-edit-btn"
              onClick={() =>
                navigate(
                  `/edit-product/${product.id}`
                )
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24px"
                viewBox="0 -960 960 960"
                width="24px"
                fill="#FFFFFF"
              >
                <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h357l-80 80H200v560h560v-278l80-80v358q0 33-23.5 56.5T760-120H200Zm160-240v-170l367-367q12-12 27-18t30-6q16 0 30.5 6t26.5 18l56 57q11 12 17 26.5t6 29.5q0 15-5.5 29.5T897-728L530-360H360Zm80-80h56l232-232-57-56-231 231v57Z" />
              </svg>

              Edit Product
            </button>
          </div>
        </div>

        {/* ================= MAIN IMAGE ================= */}

        <div className="productdetails-image-section">
          <div className="productdetails-main-image">
            {activeImg ? (
              <img
                src={activeImg}
                alt={
                  product.name ||
                  "product"
                }
              />
            ) : (
              <div className="productdetails-no-image">
                No image available
              </div>
            )}
          </div>
        </div>

        {/* ================= THUMBNAILS ================= */}

        {images.length > 0 && (
          <div className="productdetails-thumbs">
            {images.map(
              (image, index) => {
                const imageUrl =
                  getImageUrl(image);

                if (!imageUrl) {
                  return null;
                }

                return (
                  <img
                    key={
                      image?.id ||
                      `${imageUrl}-${index}`
                    }
                    src={imageUrl}
                    alt={`thumb-${index + 1}`}
                    className={
                      activeImg === imageUrl
                        ? "productdetails-thumb active"
                        : "productdetails-thumb"
                    }
                    onClick={() =>
                      setActiveImg(
                        imageUrl
                      )
                    }
                  />
                );
              }
            )}
          </div>
        )}

        {/* ================= PRODUCT BASIC INFO ================= */}

        <div className="productdetails-card new-ui">
          <div className="productdetails-card-header">
            <div>
              <h3 className="productdetails-title">
                {product.name}
              </h3>

              {product.tag && (
                <div className="productdetails-tags">
                  <span className="tag new">
                    {product.tag}
                  </span>
                </div>
              )}
            </div>

            <span
              className={`productdetails-status ${Number(product.status) === 1
                  ? "active"
                  : "out"
                }`}
            >
              {statusText}
            </span>
          </div>

          {/* ================= PRICE ================= */}

          <div className="productdetails-price-row">
            <span className="productdetails-new-price">
              ₹{Math.round(finalPrice)}
            </span>

            {hasDiscount && (
              <span className="productdetails-old-price">
                ₹{Math.round(finalPrice)}
              </span>
            )}

            {hasDiscount && (
              <span className="productdetails-discount">
                {discountPercentage}% OFF
              </span>
            )}
          </div>

          <hr className="productdetails-divider" />

          {/* DESCRIPTION */}

          <div className="productdetails-desc-block">
            <h4>
              Description
            </h4>

            <p className="productdetails-desc">
              {product.description ||
                "-"}
            </p>
          </div>
        </div>

        {/* ================= SPECIFICATIONS ================= */}

        <div className="productdetails-card spec-new">
          <h4 className="section-title">
            Specifications
          </h4>

          <div className="productdetails-spec-grid">
            {/* STOCK */}

            <div className="spec-box">
              <div className="spec-icon">
                📦
              </div>

              <div>
                <p className="productdetails-label">
                  Stock Quantity
                </p>

                <p>
                  {product.quantity ??
                    0}{" "}
                  pieces
                </p>
              </div>
            </div>

            {/* FABRIC */}

            <div className="spec-box">
              <div className="spec-icon">
                🧵
              </div>

              <div>
                <p className="productdetails-label">
                  Fabric Type
                </p>

                <p>
                  {product.material ||
                    "-"}
                </p>
              </div>
            </div>

            {/* CATEGORY */}

            <div className="spec-box">
              <div className="spec-icon">
                🏷️
              </div>

              <div>
                <p className="productdetails-label">
                  Category
                </p>

                <p>
                  {product.category
                    ?.name || "-"}
                </p>
              </div>
            </div>

            {/* ORIGINAL PRICE */}

            <div className="spec-box highlight">
              <div className="spec-icon">
                ₹
              </div>

              <div>
                <p className="productdetails-label">
                  Original Price
                </p>

                <p>
                  ₹{Math.round(finalPrice)}
                </p>
              </div>
            </div>

            {/* DISCOUNT */}

            <div className="spec-box">
              <div className="spec-icon">
                %
              </div>

              <div>
                <p className="productdetails-label">
                  Discount
                </p>

                <p>
                  {discountPercentage > 0
                    ? `${discountPercentage}%`
                    : "No Discount"}
                </p>
              </div>
            </div>

            {/* FINAL PRICE */}

            <div className="spec-box highlight">
              <div className="spec-icon">
                ₹
              </div>

              <div>
                <p className="productdetails-label">
                  Final Price
                </p>

                <p>
                  ₹{Math.round(finalPrice)}
                </p>
              </div>
            </div>

            {/* STATUS */}

            <div className="spec-box">
              <div className="spec-icon">
                ✓
              </div>

              <div>
                <p className="productdetails-label">
                  Status
                </p>

                <p>
                  {statusText}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ================= ADDITIONAL DETAILS ================= */}

        <div className="productdetails-card spec-new">
          <h4 className="section-title">
            Additional Details
          </h4>

          <div className="productdetails-spec-grid-additional">
            {/* SKU */}

            <div className="spec-box-additional">
              <p className="productdetails-label-additional">
                SKU
              </p>

              <p>
                {product.sku || "-"}
              </p>
            </div>

            {/* COLORS */}

            <div className="spec-box-additional">
              <p className="productdetails-label-additional">
                Colors
              </p>

              {colors.length > 0 ? (
                <div className="productdetails-color-list">
                  {colors.map(
                    (
                      item,
                      index
                    ) => {
                      const colorName =
                        item?.colorName ||
                        "Color";

                      const hex =
                        COLOR_HEX[
                        colorName.toLowerCase()
                        ] ||
                        "#D1D5DB";

                      return (
                        <span
                          className="productdetails-color-chip"
                          key={`${colorName}-${index}`}
                        >
                          <span
                            className="productdetails-color-dot"
                            style={{
                              backgroundColor:
                                hex,
                            }}
                          />

                          {colorName}
                        </span>
                      );
                    }
                  )}
                </div>
              ) : (
                <p>-</p>
              )}
            </div>

            {/* SIZES */}

            <div className="spec-box-additional">
              <p className="productdetails-label-additional">
                Sizes
              </p>

              {sizes.length > 0 ? (
                <div className="productdetails-size-list">
                  {sizes.map(
                    (size) => (
                      <span
                        className="productdetails-size-chip"
                        key={size}
                      >
                        {size}
                      </span>
                    )
                  )}
                </div>
              ) : (
                <p>
                  {product.size ||
                    "-"}
                </p>
              )}
            </div>

            {/* TAG */}

            <div className="spec-box-additional">
              <p className="productdetails-label-additional">
                Tag
              </p>

              <p>
                {product.tag || "-"}
              </p>
            </div>

            {/* ORIGINAL PRICE */}

            <div className="spec-box-additional">
              <p className="productdetails-label-additional">
                Original Price
              </p>

              <p>
                ₹{Math.round(finalPrice)}
              </p>
            </div>

            {/* DISCOUNT */}

            <div className="spec-box-additional">
              <p className="productdetails-label-additional">
                Discount
              </p>

              <p>
                {discountPercentage > 0
                  ? `${discountPercentage}% OFF`
                  : "No Discount"}
              </p>
            </div>

            {/* FINAL PRICE */}

            <div className="spec-box-additional">
              <p className="productdetails-label-additional">
                Final Price
              </p>

              <p>
                ₹{Math.round(finalPrice)}
              </p>
            </div>
          </div>
        </div>

        {/* ======================================================
            PRODUCT DETAILS SECTION
           ====================================================== */}

        <div className="productdetails-card productdetails-detail-card">
          <div className="productdetails-detail-heading">
            <div>
              <h4 className="section-title">
                Details
              </h4>

              <p>
                Product information shown to customers
              </p>
            </div>
          </div>

          <div className="productdetails-detail-box">
            {/* LENGTH */}

            <div className="productdetails-detail-row">
              <div className="productdetails-detail-label">
                LENGTH
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {details.length ||
                  "-"}
              </div>
            </div>

            {/* FABRIC */}

            <div className="productdetails-detail-row">
              <div className="productdetails-detail-label">
                FABRIC
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {product.material ||
                  "-"}
              </div>
            </div>

            {/* WASH CARE */}

            <div className="productdetails-detail-row">
              <div className="productdetails-detail-label">
                WASH CARE
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {details.washCare ||
                  "-"}
              </div>
            </div>

            {/* SLEEVES */}

            <div className="productdetails-detail-row">
              <div className="productdetails-detail-label">
                SLEEVES
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {details.sleeves ||
                  "-"}
              </div>
            </div>

            {/* NECKLINE */}

            <div className="productdetails-detail-row">
              <div className="productdetails-detail-label">
                NECKLINE
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {details.neckline ||
                  "-"}
              </div>
            </div>

            {/* DRESS */}

            <div className="productdetails-detail-row">
              <div className="productdetails-detail-label">
                DRESS
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {details.dressInfo ||
                  "-"}
              </div>
            </div>

            {/* DISCLAIMER */}

            <div className="productdetails-detail-row productdetails-detail-long">
              <div className="productdetails-detail-label">
                DISCLAIMER
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {details.disclaimer ||
                  "-"}
              </div>
            </div>

            {/* WHAT YOU WILL RECEIVE */}

            <div className="productdetails-detail-row">
              <div className="productdetails-detail-label">
                WHAT YOU WILL RECEIVE
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {details.whatYouWillReceive ||
                  "-"}
              </div>
            </div>

            {/* CUSTOM DETAILS */}

            {customDetails.map(
              (detail, index) => (
                <div
                  className="productdetails-detail-row"
                  key={`custom-detail-${index}`}
                >
                  <div className="productdetails-detail-label">
                    {detail.label ||
                      `DETAIL ${index + 1}`}
                  </div>

                  <div className="productdetails-detail-colon">
                    :
                  </div>

                  <div className="productdetails-detail-value">
                    {detail.value ||
                      "-"}
                  </div>
                </div>
              )
            )}

            {/* SKU */}

            <div className="productdetails-detail-row">
              <div className="productdetails-detail-label">
                SKU
              </div>

              <div className="productdetails-detail-colon">
                :
              </div>

              <div className="productdetails-detail-value">
                {product.sku ||
                  "-"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;